import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import { fmtShortClock, fmt } from '../utils/format';

// Palette (kept inline — recharts wants hex, not tailwind classes).
const COLORS = {
  purple: '#848cba',
  red: '#e84855',
  grid: '#e5e7eb',
  axis: '#6b7280',
};

// LineChart of processed vs failed jobs, 1-minute buckets, last 15 min.
// Series comes straight from API (15 entries, oldest first).
export const ThroughputChart = ({ series = [] }) => {
  const data = useMemo(
    () =>
      (series || []).map(row => ({
        time: fmtShortClock(row.t),
        processed: row.processed ?? 0,
        failed: row.failed ?? 0,
      })),
    [series]
  );

  const hasFailed = useMemo(
    () => (series || []).some(row => (row.failed ?? 0) > 0),
    [series]
  );

  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-[200px] text-xs text-darkGrayColor">
        No throughput data yet.
      </div>
    );
  }

  return (
    <ResponsiveContainer
      width="100%"
      height={200}
    >
      <LineChart
        data={data}
        margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={COLORS.grid}
        />
        <XAxis
          dataKey="time"
          stroke={COLORS.axis}
          fontSize={11}
          tickMargin={4}
        />
        <YAxis
          stroke={COLORS.axis}
          fontSize={11}
          allowDecimals={false}
          tickFormatter={v => fmt(v)}
          width={40}
        />
        <Tooltip
          contentStyle={{ fontSize: 12 }}
          formatter={value => fmt(value)}
        />
        {hasFailed && (
          <Legend
            verticalAlign="top"
            height={24}
            wrapperStyle={{ fontSize: 11 }}
          />
        )}
        <Line
          type="monotone"
          dataKey="processed"
          name="Processed"
          stroke={COLORS.purple}
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4 }}
        />
        {hasFailed && (
          <Line
            type="monotone"
            dataKey="failed"
            name="Failed"
            stroke={COLORS.red}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};
